
import { MenuItem } from '@headlessui/vue'
import { LockClosedIcon } from '@heroicons/vue/20/solid'
import { ClipboardDocumentCheckIcon, InformationCircleIcon } from '@heroicons/vue/20/solid'
import { CheckIcon, ExclamationTriangleIcon, PencilIcon, PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import _ from 'lodash'
import { DatePicker } from 'v-calendar'
import { computed, onMounted, reactive, ref } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import DropsWithOrdersModal from '@/components/neue/DropsWithOrdersModal.vue'
import EmptyState from '@/components/neue/EmptyState.vue'
import NeueWell from '@/components/neue/NeueWell.vue'
import SettingsSection from '@/components/neue/SettingsSection.vue'
import { useFulfillmentWizardAccess } from '@/composables/useFulfillmentWizardAccess'
import { useRouteTabs } from '@/composables/useRouteTabs'
import { useStagedRecords } from '@/composables/useStagedRecords'
import { useToast } from '@/composables/useToast'
import { useUnloadGuard } from '@/composables/useUnloadGuard'
import { WIZARD_PATH as FULFILLMENT_WIZARD_PATH } from '@/config/fulfillmentWizardSteps'
import { audienceConfirmRows } from '@/helpers/autocharge'
import { dateObjectToServerDay, serverDayToDateObject } from '@/helpers/dates'
import { deletedDropIdsFrom, dropImpactFrom, updateNeedsConfirming } from '@/helpers/dropImpact'
import {
  defaultReminderDate,
  editableReminderText,
  hasReminderText,
  REMINDER_TEXT_PLACEHOLDER,
  REMINDER_TEXT_REQUIRED_WARNING,
  reminderAutomationAttributes
} from '@/helpers/dropReminders'
import {
  DUPLICATOR_DEFAULT_INTERVAL,
  duplicatorAttributes,
  duplicatorRecurrenceDescription
} from '@/helpers/duplicator'
import { serverFilterLabel } from '@/helpers/filters'
import { isLocalId, mergeNestedAttributes, serializeNestedAttributes } from '@/helpers/nestedAttributes'
import api from '@/store/api'
import { mapRelationship, mapRelationships, mapType } from '@/store/mappers'

import ExplanationBox from '../../components/ExplanationBox.vue'
import InlineSearchSelect from '../../components/InlineSearchSelect.vue'
import ImageUploaderNeue from '../../components/neue/ImageUploaderNeue.vue'
import NeueButton from '../../components/neue/NeueButton.vue'
import VariantRow from '../../components/neue/VariantRow.vue'

const DROP_TABS = [
  { id: 'details', name: 'Details' },
  { id: 'customers', name: 'Customers' },
  { id: 'production-days', name: 'Production days' },
  { id: 'reminders', name: 'Reminders' },
  { id: 'autofill', name: 'Autofill' },
  { id: 'autocharge', name: 'Autocharge' },
  { id: 'actions', name: 'Actions' }
]

export default {
  components: {
    SettingsSection,
    DropsWithOrdersModal,
    PencilIcon,
    PlusIcon,
    XMarkIcon,
    CheckIcon,
    ExclamationTriangleIcon,
    MenuItem,
    InformationCircleIcon,
    ClipboardDocumentCheckIcon,
    DatePicker,
    LockClosedIcon,
    MenuItem,
    ExplanationBox,
    ImageUploaderNeue,
    InlineSearchSelect,
    NeueButton,
    NeueWell,
    EmptyState,
    VariantRow
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const vStore = useStore()
    const wizardEnabled = useFulfillmentWizardAccess()
    const { activeTab, sections: dropSections } = useRouteTabs(
      DROP_TABS,
      computed(() => `/store/drops/${route.params.id}`),
      { keepHash: true }
    )
    const { showToast } = useToast()

    onBeforeRouteLeave(() => {
      if (hasUnsavedChanges.value) {
        drop.leaveModal = true
        return false
      } else return true
    })

    const drop = reactive({
      id: router.currentRoute._rawValue.params.id,
      description: '',
      loading: true,
      // The drop is gone (deleted, or not this merchant's).
      missing: false,
      leaveModal: false
    })

    const modal = reactive({
      visible: false,
      date: null,
      message: null,
      time: true,
      interval_type: null,
      interval_number: null,
      has_duplicated: false,
      attachmentType: 'image'
    })

    const cartItemsModal = reactive({
      open: false,
      searchQuery: '',
      searchResults: [],
      selectedProducts: []
    })

    const openVariantTray = ref(null)

    const vDrop = computed(() => vStore.state.objects.bottle_cycle?.[drop.id])

    // Deleting a drop removes it from the store while this page is still
    // mounted, so every computed below has to survive vDrop being undefined.
    const vDropErrors = computed(() => vDrop.value?.attributes.bottle_cycle_errors || {})

    const storeError = computed(() => {
      if (vDropErrors.value.no_store_error) {
        return router.currentRoute._rawValue.hash === '#new'
          ? vDropErrors.value.no_store_error.error_friendly
          : vDropErrors.value.no_store_error.error
      }
    })

    const groupsError = computed(() => {
      if (vDropErrors.value.no_group_error) {
        const errorText =
          router.currentRoute._rawValue.hash === '#new'
            ? vDropErrors.value.no_group_error.error_friendly
            : vDropErrors.value.no_group_error.error
        return errorText?.replace(/\bgroups\b/gi, 'smart lists').replace(/\bgroup\b/gi, 'smart list')
      }
    })

    const fulfillmentAvailabilityDaysError = computed(() => {
      if (vDropErrors.value.no_fulfillment_availability_days_error) {
        return router.currentRoute._rawValue.hash === '#new'
          ? vDropErrors.value.no_fulfillment_availability_days_error.error_friendly
          : vDropErrors.value.no_fulfillment_availability_days_error.error
      }
    })

    const errorClasses = computed(() => {
      return router.currentRoute._rawValue.hash === '#new' ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'
    })

    const vAllStores = computed(() => Object.values(vStore.state.objects.store || {}))

    const vSelectedStore = computed(
      () => vDrop.value?.relationships.store.data && mapRelationship(vDrop.value.relationships.store.data)
    )

    const vAudiences = computed(() => Object.values(vStore.state.objects.audience || {}))

    const vAudiencePairs = computed(() =>
      vDrop.value ? mapRelationships(vDrop.value.relationships.bottle_cycle_audience_pairs.data) : []
    )

    const vSelectedAudiences = computed(() =>
      vAudiencePairs.value.map(x => mapRelationship({ type: 'audience', id: x.attributes.audience_id })).filter(x => x)
    )

    const vTierAudiences = computed(() =>
      mapType('membership_tier')
        .map(tier => tier.relationships?.audience?.data && mapRelationship(tier.relationships.audience.data))
        .filter(Boolean)
        .sort((a, b) => a.attributes.audience_name.localeCompare(b.attributes.audience_name))
    )

    const vSuggestedTierAudiences = computed(() =>
      vTierAudiences.value.filter(audience => !vSelectedAudiences.value.some(x => x.id === audience.id))
    )

    const readableAudiencesNames = computed(() => {
      const audiencesNames = vSelectedAudiences.value.map(audience => {
        return audience.attributes.audience_name
      })
      if (!audiencesNames.length) {
        return ''
      } else if (audiencesNames.length === 1) {
        return `${audiencesNames[0]} smart list`
      } else {
        return `${customJoin(audiencesNames, ', ', ' and ')} smart lists`
      }
    })
    const readableRemindersTimes = computed(() => {
      const reminderTimes = vReminders.value.map(reminder => {
        return format(vScheduler(reminder))
      })
      if (!reminderTimes.length) {
        return ''
      } else if (reminderTimes.length === 1) {
        return `${reminderTimes[0]}`
      } else {
        return `${customJoin(reminderTimes, ', ', ' and ')}`
      }
    })
    const readablePossibleFulfillmentDaysText = computed(() => {
      const possibleFulfillmentDaysText = vDays.value.map(day => {
        return formatDay(day.attributes.date)
      })
      if (!possibleFulfillmentDaysText.length) {
        return ''
      } else if (possibleFulfillmentDaysText.length === 1) {
        return `${possibleFulfillmentDaysText[0]}`
      } else {
        return `${customJoin(possibleFulfillmentDaysText, ', ', ' and ')}`
      }
    })
    const customJoin = (arr, s1, s2) => {
      return arr
        .slice(0, -1)
        .join(s1)
        .concat(arr.length > 1 ? s2 : '', arr.slice(-1))
    }
    const displayReminderAutomationMessage = computed(() => {
      return false
      // return (
      //   !!readableAudiencesNames.value && !!readableRemindersTimes.value && !!readablePossibleFulfillmentDaysText.value
      // )
    })
    const displayAutoChargeAutomationMessage = computed(() => {
      // return !!readableAudiencesNames.value && !!vAutocharge.value && !!readablePossibleFulfillmentDaysText.value
      return false
    })
    const displayAutoFillAutoChargeAutomationMessage = computed(() => {
      // return (
      //   !!readableAudiencesNames.value && !!isAutofillAutocharge.value && !!readablePossibleFulfillmentDaysText.value
      // )
      return false
    })
    const displayAutoFillReminderAutomationMessage = computed(() => {
      // return (
      //   !!readableAudiencesNames.value && !!isAutofillReminders.value && !!readablePossibleFulfillmentDaysText.value
      // )
      return false
    })
    const reminderAutomationMessage = computed(() => {
      return `All customers in the ${readableAudiencesNames.value} at ${readableRemindersTimes.value} will receive this reminder unless the customer has already skipped or already paid you for an order spanning ${readablePossibleFulfillmentDaysText.value}.`
    })
    const autoChargeAutomationMessage = computed(() => {
      return `All customers in the ${readableAudiencesNames.value} at ${format(
        vScheduler(vAutocharge.value)
      )} will be automatically charged unless the customer has already skipped or already paid you for an order spanning ${
        readablePossibleFulfillmentDaysText.value
      }.`
    })
    const autoFillAutoChargeAutomationMessage = computed(() => {
      return `All customers in the ${readableAudiencesNames.value} at ${format(
        vScheduler(vAutocharge.value)
      )} will have their orders automatically filled before auto charging unless the customer has already skipped or already paid you for an order spanning ${
        readablePossibleFulfillmentDaysText.value
      }.`
    })
    const autoFillReminderAutomationMessage = computed(() => {
      return `All customers in the ${readableAudiencesNames.value} at ${readableRemindersTimes.value} will have their orders automatically filled before reminders are sent out unless the customer has already skipped or already paid you for an order spanning ${readablePossibleFulfillmentDaysText.value}.`
    })

    const vAudienceQuery = ref('')
    const vAudienceResults = reactive([])

    const vAudienceDetail = audience => mapRelationship(audience.relationships.audience_detail.data)

    const vAudienceFilters = audience =>
      mapRelationships(audience.relationships.filter_groups.data).flatMap(x =>
        mapRelationships(x.relationships.filters.data)
      )

    const vFilterString = serverFilterLabel

    const vScheduler = object => {
      if (object) {
        return (
          object.relationships &&
          new Date(mapRelationship(object.relationships.automation_scheduler.data).attributes.datetime)
        )
      }
    }

    const vTemplate = reminder => mapRelationship(reminder.relationships.message_template.data)

    const vMessage = reminder => vTemplate(reminder).attributes.body

    const vImages = reminder => mapRelationships(vTemplate(reminder).relationships.attachments.data)

    const vImage = reminder => vImages(reminder)[0]?.attributes

    const vAutofill = computed(() => {
      const rel = vDrop.value?.relationships.bottle_autofill_automation.data
      if (rel) return mapRelationship(rel)
    })

    const vAutofillFields = computed(() =>
      vAutofill.value ? mapRelationships(vAutofill.value.relationships.automation_fields.data) : []
    )

    const vAutofillType = computed(
      () => vAutofillFields.value.find(x => x.attributes.key === 'type')?.attributes.text_value
    )

    const vCart = computed(() => mapRelationship(vDrop.value?.relationships.cart?.data))
    const vCartItems = computed(() => mapRelationships(vCart.value?.relationships.cart_items?.data))

    const isAutofillReminders = computed(() =>
      vAutofillFields.value.some(x => x.attributes.key === 'run_before' && x.attributes.text_value === 'reminders')
    )

    const isAutofillAutocharge = computed(() =>
      vAutofillFields.value.some(x => x.attributes.key === 'run_before' && x.attributes.text_value === 'autocharge')
    )

    const vAutocharge = computed(() => {
      const rel = vDrop.value?.relationships.bottle_autocharge_automation.data
      if (rel) return mapRelationship(rel)
    })

    const vDuplicator = computed(() => {
      const duplicatorData = vDrop.value?.relationships.duplicator.data
      if (duplicatorData) return mapRelationship(duplicatorData)
    })

    const vDuplicatorOptions = computed(() => [
      ['weeks', 'Week(s)'],
      ['days', 'Day(s)']
    ])

    const vReminders = computed(() => mapRelationships(vDrop.value?.relationships.bottle_reminder_automations.data))

    const vDays = computed(() => mapRelationships(vDrop.value?.relationships.fulfillment_availabilities.data))

    // Every edit is staged rather than saved: written into the cache so the
    // page shows it at once, and folded into the one body that Save sends. The
    // cache copy of each touched record is kept so that Discard can put it
    // back, and a record created here lives under a local id until the save's
    // response brings the real one.
    const staged = useStagedRecords(vStore)
    const pending = ref({})
    const hasUnsavedChanges = computed(() => Object.keys(pending.value).length > 0)
    useUnloadGuard(hasUnsavedChanges)

    // The modals used to save the moment they closed; now they only stage.
    // Said once per visit, the first time a modal closes on a staged edit, so
    // a merchant who learnt the old page knows the footer Save is still to come.
    const UNSAVED_HINT = 'Not saved yet: click Save at the bottom of the page to apply your changes.'
    let unsavedHintShown = false
    const hintUnsaved = () => {
      if (unsavedHintShown) return
      unsavedHintShown = true
      showToast(UNSAVED_HINT, { type: 'info', duration: 8000 })
    }
    const stage = change => {
      pending.value = mergeNestedAttributes(pending.value, change)
    }

    // For a block the server rebuilds from scratch on every save, the body
    // carries the whole current state rather than a merge of edits.
    const replaceStaged = change => {
      pending.value = { ...pending.value, ...change }
    }

    const recordRef = record => ({ type: record.type, id: record.id })
    const dropRecord = () => ({ type: 'bottle_cycle', id: drop.id })
    const sameId = (a, b) => String(a) === String(b)

    // A record created on this page has no server id yet; the body names it by
    // its local one until then.
    const identity = record => (isLocalId(record.id) ? { _localId: record.id } : { id: record.id })

    const patchDrop = patch => staged.patchRecord('bottle_cycle', drop.id, patch)

    // The related records as cached, including any hidden by a staged delete.
    const relatedRecords = (record, relationship) =>
      (record?.relationships?.[relationship]?.data || []).map(x => staged.read(x.type, x.id)).filter(Boolean)

    const hidden = record => record.attributes?.is_active === false

    // Stages adding a record to a has_many. `entries` shapes the list into the
    // body, since the same list can sit at different depths.
    const stageAddition = ({ parent, relationship, record, entry, entries }) => {
      const added = staged.addRecord({
        type: record.type,
        attributes: { is_active: true, ...record.attributes },
        relationships: record.relationships
      })
      staged.linkMany(parent.type, parent.id, relationship, added)
      stage(entries([{ _localId: added.id, ...entry }]))
      return added
    }

    // Stages removing a record from a has_many. One created on this page is
    // simply forgotten; one the server knows is hidden until the save removes it.
    const stageRemoval = ({ parent, relationship, record, entries }) => {
      if (staged.isTemp(record.type, record.id)) {
        staged.unlinkMany(parent.type, parent.id, relationship, record)
        staged.forgetRecord(record.type, record.id)
        stage(entries([{ _localId: record.id, _destroy: true }]))
      } else {
        staged.patchRecord(record.type, record.id, { attributes: { is_active: false } })
        stage(entries([{ id: record.id, _destroy: true }]))
      }
    }

    // Takes a staged removal back: the record shows again and nothing is sent.
    const stageRestoration = ({ record, entries }) => {
      staged.restoreRecord(record.type, record.id)
      stage(entries([{ id: record.id, _destroy: false }]))
    }

    // Turning a has_one off. The drop stops pointing at it; if the server has
    // a record there it is destroyed, otherwise the addition is withdrawn. A
    // record created here can be standing in for one the server still has
    // (deleted and set again before saving), so the loaded copy decides.
    const stageHasOneRemoval = ({ relationship, record, key, withId = false }) => {
      patchDrop({ relationships: { [relationship]: { data: null } } })
      if (staged.isTemp(record.type, record.id)) staged.forgetRecord(record.type, record.id)
      const loaded = staged.original('bottle_cycle', drop.id)?.relationships?.[relationship]?.data
      if (!loaded) return stage({ [key]: { _destroy: false } })
      stage({ [key]: withId ? { id: loaded.id, _destroy: true } : { _destroy: true } })
    }

    const reminderEntries = list => ({ bottle_reminder_automations_attributes: list })
    const dayEntries = list => ({ fulfillment_availabilities_attributes: list })
    const pairEntries = list => ({ bottle_cycle_audience_pairs_attributes: list })
    const autofillFieldEntries = list => ({
      bottle_autofill_automation_attributes: { automation_fields_attributes: list }
    })
    const cartEntries = list => ({ cart_attributes: { cart_items_attributes: list } })

    // Whether the saved drop repeats. Only the server's copy counts: a repeat
    // rule staged here has generated no later drops yet, and a staged removal
    // has torn none down.
    const chained = ref(false)

    // After a load or a save the server's copy is the truth again.
    const settleAfterLoad = response => {
      staged.settle()
      pending.value = {}
      const record = response?.data?.data
      drop.description = record?.attributes?.description
      chained.value = Boolean(record?.relationships?.duplicator?.data)
    }

    const discardStaged = () => {
      staged.restoreAll()
      pending.value = {}
      drop.description = vDrop.value?.attributes.description
    }

    const setDescription = value => {
      drop.description = value
      stage({ description: value })
    }

    const newReminder = () => setModal({ type: 'bottle_reminder_automation' })

    function hasDuplicate() {
      if (vDuplicator.value) {
        return vDuplicator.value.attributes.has_duplicated
      }
      return false
    }

    function duplicationText() {
      if (this.hasDuplicate()) {
        return 'Every ~1 hour, the system checks to see if it should make a copy of this drop.'
      } else {
        return "Every ~1 hour, the system checks to see if it should make a copy of this drop. Currently it's pending to duplicate."
      }
    }

    const newDay = () => {
      modal.object = { type: 'fulfillment_availability' }
      modal.date = null
      modal.time = false
      modal.message = null
      modal.images = []
      modal.visible = true
    }

    // A repeating drop is a chain of real drops rather than one recurring
    // record, so a save has to say whether it is for this drop or for this and
    // every later drop in the chain. Asked once, when the merchant saves.
    const scopeModal = reactive({ visible: false, resolve: null })

    const askScope = () => {
      if (!chained.value) return Promise.resolve(false)
      scopeModal.visible = true
      return new Promise(resolve => {
        scopeModal.resolve = resolve
      })
    }

    // null backs out of the save entirely.
    const chooseScope = applyToFuture => {
      const { resolve } = scopeModal
      scopeModal.visible = false
      scopeModal.resolve = null
      if (resolve) resolve(applyToFuture)
    }

    const dropUrl = (applyToFuture, query) => {
      const params = [query, applyToFuture ? 'cascade=true' : ''].filter(Boolean).join('&')
      return params ? `bottle_cycles/${drop.id}?${params}` : `bottle_cycles/${drop.id}`
    }

    // A drop still being created carries #new in the route, the same signal the
    // status badge reads. Its chain was generated from an empty drop the moment
    // it was created, so the first save has to reach the whole chain, and there
    // is nothing to ask.
    const stillBeingCreated = () => router.currentRoute.value.hash === '#new'

    // Which later drops the save applies to. Resolves to null when the merchant
    // backs out, leaving the edits staged.
    const chooseCascade = async () => {
      if (stillBeingCreated()) return true
      const applyToFuture = await askScope()
      if (applyToFuture === null) return null
      // An "all future" save tears the later drops down and rebuilds them, so
      // the merchant confirms against what that costs before it runs.
      if (applyToFuture && !(await confirmImpact('update', true))) return null
      return applyToFuture
    }

    const saving = ref(false)

    // Sends every staged edit in one request. The response carries the whole
    // drop, so the cache is the server's copy again afterwards. A failed save
    // leaves the edits staged for another try.
    const saveDrop = async () => {
      if (!hasUnsavedChanges.value || saving.value) return
      const cascade = await chooseCascade()
      if (cascade === null) {
        // Backing out of the question is not a save, and the page would
        // otherwise go quiet with the edits still sitting there.
        showToast('Nothing was saved. Your changes are still here, click Save when you are ready.', {
          type: 'info',
          duration: 8000
        })
        return
      }
      saving.value = true
      try {
        const response = await api.call('PUT', dropUrl(cascade, `included=${dropIncludes()}`), {
          bottle_cycle: serializeNestedAttributes(pending.value)
        })
        settleAfterLoad(response)
        // The first save of a new drop fills its whole chain. From here on it
        // is an ordinary repeating drop, and later saves should ask.
        if (stillBeingCreated()) router.replace(`/store/drops/${drop.id}`)
      } catch (error) {
        showToast(error?.response?.data?.errors?.[0] || 'Those changes could not be saved.', {
          type: 'error',
          duration: 8000
        })
      } finally {
        saving.value = false
      }
    }

    const deleteReminder = reminder =>
      stageRemoval({
        parent: dropRecord(),
        relationship: 'bottle_reminder_automations',
        record: reminder,
        entries: reminderEntries
      })

    const deleteDay = day =>
      stageRemoval({
        parent: dropRecord(),
        relationship: 'fulfillment_availabilities',
        record: day,
        entries: dayEntries
      })

    const deleteAutofill = () => {
      if (!vAutofill.value) return
      stageHasOneRemoval({
        relationship: 'bottle_autofill_automation',
        record: vAutofill.value,
        key: 'bottle_autofill_automation_attributes'
      })
    }

    const deleteDuplicator = () => {
      if (!vDuplicator.value) return
      stageHasOneRemoval({
        relationship: 'duplicator',
        record: vDuplicator.value,
        key: 'duplicator_attributes',
        withId: true
      })
    }

    const deleteAutocharge = () => {
      if (!vAutocharge.value) return
      stageHasOneRemoval({
        relationship: 'bottle_autocharge_automation',
        record: vAutocharge.value,
        key: 'bottle_autocharge_automation_attributes'
      })
    }

    const showDeleteModal = ref(false)

    // What the pending delete or "all future" save would cost, read from the
    // server before the write. Shown in a modal rather than a toast: the
    // merchant needs to read which drops, and follow a link to the orders on
    // them, then confirm twice.
    const ordersModal = reactive({ visible: false, drops: [], totals: {}, context: 'delete', resolve: null })

    // Resolves true once the merchant has confirmed, false if they back out.
    // A delete always asks. A save only asks when the rebuild would cost
    // something, because the page saves on every field edit.
    const confirmImpact = async (context, cascade) => {
      const query = [cascade ? 'cascade=true' : '', context === 'update' ? 'intent=update' : '']
        .filter(Boolean)
        .join('&')
      let impact
      try {
        // receive: false - the body is a meta-only preview, not a drop record.
        impact = dropImpactFrom(
          await api.call('GET', `bottle_cycles/${drop.id}/impact?${query}`, null, { receive: false })
        )
      } catch (error) {
        // Without the impact we cannot describe the consequences, so ask rather
        // than silently proceeding.
        impact = { drops: [], totals: { affected_drop_count: 0 } }
      }
      if (context === 'update' && !updateNeedsConfirming(impact)) return true

      ordersModal.drops = impact.drops
      ordersModal.totals = impact.totals
      ordersModal.context = context
      ordersModal.visible = true
      return new Promise(resolve => {
        ordersModal.resolve = resolve
      })
    }

    const settleOrdersModal = confirmed => {
      const { resolve } = ordersModal
      ordersModal.visible = false
      ordersModal.resolve = null
      if (resolve) resolve(confirmed)
    }

    const closeOrdersModal = () => settleOrdersModal(false)
    const confirmOrdersModal = () => settleOrdersModal(true)

    const deleteDrop = () => {
      if (chained.value) {
        showDeleteModal.value = true
      } else {
        performDelete(false)
      }
    }

    const performDelete = async cascade => {
      showDeleteModal.value = false
      if (!(await confirmImpact('delete', cascade))) return

      const url = cascade ? `bottle_cycles/${drop.id}?cascade=true` : `bottle_cycles/${drop.id}`
      try {
        const response = await api.call('DELETE', url)
        // Whatever was staged went with the drop; a failed delete keeps it.
        staged.settle()
        pending.value = {}
        // api.call drops the drop named in the URL. A cascade also removed the
        // rest of the chain server-side, and the drops list reads straight from
        // the store, so those stale copies have to go too.
        deletedDropIdsFrom(response).forEach(id =>
          vStore.commit('DESTROY_OBJECT', { data: { type: 'bottle_cycle', id } })
        )
        router.push('/store/drops')
      } catch (error) {
        showToast(error?.response?.data?.errors?.[0] || 'That drop could not be deleted.', {
          type: 'error',
          duration: 8000
        })
      }
    }

    const selectStore = store => {
      patchDrop({ attributes: { store_id: Number(store.id) }, relationships: { store: { data: recordRef(store) } } })
      stage({ store_id: store.id })
      loadProductionDates(store.id)
    }

    const setPreference = pref => {
      patchDrop({ attributes: { store_preference: pref } })
      stage({ store_preference: pref })
    }

    const setDuplicatorProperty = (property, option) => {
      modal[property] = option
    }

    function showDuplicator() {
      return vDays.value.length > 0 || Boolean(vDuplicator.value)
    }

    function isOriginalDuplication(item) {
      if (item) {
        return item.attributes.display_description !== 'Duplicated'
      }
      return false
    }

    function duplicatorDescription() {
      return duplicatorRecurrenceDescription(vDuplicator.value?.attributes)
    }
    function duplicatorIntervalType() {
      if (modal.interval_type) {
        if (typeof modal.interval_type === 'object') {
          return modal.interval_type[1]
        } else {
          return this.indexForInterval(modal.interval_type)[1]
        }
      } else {
        return 'Select interval type'
      }
    }

    const intervalChanged = (value, property) => {
      modal[property] = value
    }

    const isAudienceSelected = audience => vSelectedAudiences.value.includes(audience)

    const applyRemoveAudience = audience => {
      const pair = vAudiencePairs.value.find(x => sameId(x.attributes.audience_id, audience.id))
      if (!pair) return
      stageRemoval({
        parent: dropRecord(),
        relationship: 'bottle_cycle_audience_pairs',
        record: pair,
        entries: pairEntries
      })
    }

    const applyAddAudience = audience => {
      api.call('GET', `audiences/${audience.id}/audience_details`).catch(() => {})
      // The card renders from the cache, and a list picked from search may not
      // be in it yet.
      if (!staged.read('audience', audience.id)) vStore.commit('RECEIVE_OBJECT', { data: audience })
      // Removed and added back before saving: the server's pair simply stays.
      const removed = relatedRecords(vDrop.value, 'bottle_cycle_audience_pairs').find(
        pair => hidden(pair) && sameId(pair.attributes.audience_id, audience.id)
      )
      if (removed) return stageRestoration({ record: removed, entries: pairEntries })
      stageAddition({
        parent: dropRecord(),
        relationship: 'bottle_cycle_audience_pairs',
        record: {
          type: 'bottle_cycle_audience_pair',
          attributes: { bottle_cycle_id: Number(drop.id), audience_id: Number(audience.id) }
        },
        entry: { audience_id: audience.id },
        entries: pairEntries
      })
    }

    // When a drop has auto-charge enabled, changing its smart lists changes who
    // gets auto-charged, so require the acknowledgement modal before applying the
    // change. Without auto-charge, apply immediately as before.
    const audienceChangeConfirm = reactive({ open: false })
    const pendingAudienceChange = ref(null)

    const audienceDetailOrNull = audience => {
      const rel = audience && audience.relationships && audience.relationships.audience_detail
      return rel && rel.data ? mapRelationship(rel.data) : undefined
    }

    const audienceChangeRows = computed(() => {
      const change = pendingAudienceChange.value
      if (!change) return []
      let resulting
      if (change.isRemove) {
        resulting = vSelectedAudiences.value.filter(a => a.id !== change.audience.id)
      } else {
        const added = mapRelationship({ type: 'audience', id: change.audience.id }) || change.audience
        resulting = [...vSelectedAudiences.value, added]
      }
      return audienceConfirmRows(resulting, audienceDetailOrNull)
    })

    const toggleAudience = audience => {
      const isRemove = isAudienceSelected(audience)
      if (vAutocharge.value) {
        // Load the added list's member count so the preview shows a real number.
        if (!isRemove) api.call('GET', `audiences/${audience.id}?included=audience_detail`)
        pendingAudienceChange.value = { audience, isRemove }
        audienceChangeConfirm.open = true
      } else if (isRemove) {
        applyRemoveAudience(audience)
      } else {
        applyAddAudience(audience)
      }
    }

    const confirmAudienceChange = () => {
      const change = pendingAudienceChange.value
      audienceChangeConfirm.open = false
      pendingAudienceChange.value = null
      if (!change) return
      if (change.isRemove) applyRemoveAudience(change.audience)
      else applyAddAudience(change.audience)
    }

    const cancelAudienceChange = () => {
      audienceChangeConfirm.open = false
      pendingAudienceChange.value = null
    }

    // The autofill automation comes into being with its first field; the
    // server does the same (update_only), so the body never names it.
    const autofillRecord = () => {
      if (vAutofill.value) return vAutofill.value
      const automation = staged.addRecord({
        type: 'bottle_autofill_automation',
        attributes: { status: 'active', is_active: true },
        relationships: { automation_fields: { data: [] } }
      })
      patchDrop({ relationships: { bottle_autofill_automation: { data: recordRef(automation) } } })
      return automation
    }

    const fieldMatches = (key, textValue) => field =>
      field.attributes.key === key && field.attributes.text_value === textValue

    const addAutofillField = (key, textValue) => {
      const automation = autofillRecord()
      // Unticked and ticked again before saving: the server's field simply stays.
      const removed = relatedRecords(automation, 'automation_fields').find(
        field => hidden(field) && fieldMatches(key, textValue)(field)
      )
      if (removed) return stageRestoration({ record: removed, entries: autofillFieldEntries })
      stageAddition({
        parent: automation,
        relationship: 'automation_fields',
        record: { type: 'automation_field', attributes: { key, text_value: textValue } },
        entry: { key, text_value: textValue },
        entries: autofillFieldEntries
      })
    }

    const removeAutofillFields = fields =>
      fields.forEach(field =>
        stageRemoval({
          parent: vAutofill.value,
          relationship: 'automation_fields',
          record: field,
          entries: autofillFieldEntries
        })
      )

    const updateAutofillCheckboxes = (which, e) => {
      if (e.target.checked) addAutofillField('run_before', which)
      else removeAutofillFields(vAutofillFields.value.filter(fieldMatches('run_before', which)))
    }

    const updateAutofillType = type => {
      removeAutofillFields(
        vAutofillFields.value.filter(x => x.attributes.key === 'type' && x.attributes.text_value !== type)
      )
      if (!vAutofillFields.value.some(fieldMatches('type', type))) addAutofillField('type', type)
    }

    const updateTipAutofill = value => {
      patchDrop({ attributes: { autofill_tip: value } })
      stage({ autofill_tip: value })
    }

    const openCartItemsModal = () => {
      cartItemsModal.open = true
      cartItemsModal.searchQuery = ''
      cartItemsModal.searchResults = []
      cartItemsModal.selectedProducts = []
    }

    // Where a product sits in the selected store, which is what a cart item
    // points at.
    const storePairIdFor = product => {
      const pairs = product?.relationships?.category_product_store_pairs?.data || []
      const match = pairs.find(
        pair => parseInt(mapRelationship(pair)?.attributes?.store_id) === parseInt(vSelectedStore.value?.id)
      )
      return match?.id
    }

    // The drop has no cart until something is put in it; the server creates
    // one from the first item the same way.
    const cartRecord = () => {
      if (vCart.value) return vCart.value
      const cart = staged.addRecord({ type: 'cart', attributes: {}, relationships: { cart_items: { data: [] } } })
      patchDrop({ relationships: { cart: { data: recordRef(cart) } } })
      return cart
    }

    const variantOptionsAttributes = item =>
      (item.attributes.variant_options_selection || []).map(({ variant_id, variant_option_id, quantity }) => ({
        variant_id,
        variant_option_id,
        quantity
      }))

    // The server builds a fresh cart from every save (the cart is a has_one
    // without update_only, and cart item ids are not permitted), so the body
    // always carries every item as it is in the cache now.
    const cartItemEntry = item => {
      const pairId = storePairIdFor(product(item))
      return {
        type: 'CategoryProductStoreCartItem',
        quantity: item.attributes.quantity,
        ...(pairId ? { category_product_store_pair_id: pairId } : {}),
        cart_item_variant_options_attributes: variantOptionsAttributes(item)
      }
    }

    const stageCart = () => replaceStaged(cartEntries(vCartItems.value.map(cartItemEntry)))

    const getCartApiIncludes = () => [
      'cart',
      'cart.cart_items',
      'cart.cart_items.product',
      'cart.cart_items.product.attachments',
      'cart.cart_items.product.variants',
      'cart.cart_items.product.variants.variant_options',
      'cart.cart_items.cart_item_variant_options',
      'cart.cart_items.product.category_product_store_pairs',
      'cart.cart_items.product.category_product_store_pairs.store'
    ]

    // Everything the page renders, so a load and a save's response both leave
    // the cache complete.
    const dropIncludes = () =>
      [
        ...getCartApiIncludes(),
        'audiences',
        'bottle_cycle_audience_pairs',
        'fulfillment_availabilities',
        'store',
        'duplicator',
        'bottle_autofill_automation',
        'bottle_autofill_automation.automation_fields',
        'bottle_autocharge_automation',
        'bottle_autocharge_automation.automation_scheduler',
        'bottle_reminder_automations',
        'bottle_reminder_automations.automation_scheduler',
        'bottle_reminder_automations.message_template',
        'bottle_reminder_automations.message_template.attachments'
      ].join(',')

    const saveCartItemsModal = () => {
      if (cartItemsModal.selectedProducts.length === 0) {
        cartItemsModal.open = false
        return
      }
      const cart = cartRecord()
      cartItemsModal.selectedProducts.forEach(item => {
        // The row renders the product from the cache. Search put it there, but
        // make sure.
        if (!staged.read('product', item.id)) vStore.commit('RECEIVE_OBJECT', { data: item })
        const added = staged.addRecord({
          type: 'cart_item',
          attributes: { quantity: 1, is_active: true, variant_options_selection: [] },
          relationships: { product: { data: recordRef(item) } }
        })
        staged.linkMany(cart.type, cart.id, 'cart_items', added)
      })
      stageCart()
      // Cart items only autofill through a CartAutofill; the field is added once.
      if (!vAutofillFields.value.some(fieldMatches('type', 'CartAutofill'))) addAutofillField('type', 'CartAutofill')
      cartItemsModal.open = false
    }

    const image = item => {
      if (item.attributes.image_url) {
        return item.attributes.image_url
      }
      return null
    }

    // A product picked from search carries fewer relationships than one the
    // drop was loaded with, so every read here is optional.
    const product = item => mapRelationship(item?.relationships?.product?.data)
    const cartItemImage = product =>
      mapRelationships(product?.relationships?.attachments?.data)[0]?.attributes.secure_url
    const variants = product => mapRelationships(product?.relationships?.variants?.data)

    const setVariantSelection = (item, selection) => {
      staged.patchRecord(item.type, item.id, { attributes: { variant_options_selection: selection } })
      stageCart()
    }

    const sameOption = (variant, option) => x =>
      sameId(x.variant_id, variant.id) && sameId(x.variant_option_id, option.id)

    const setOption = (item, variant, option) => {
      let selection = variantOptionsAttributes(item)
      if (variant.attributes.quantity_option === 'choose_one') {
        selection = selection.filter(x => !sameId(x.variant_id, variant.id))
        selection.push({ variant_id: variant.id, variant_option_id: option.id })
      } else if (selection.some(sameOption(variant, option))) {
        selection = selection.filter(x => !sameOption(variant, option)(x))
      } else {
        selection.push({ variant_id: variant.id, variant_option_id: option.id })
      }
      setVariantSelection(item, selection)
    }

    const setVariantQuantity = (item, variant, option, quantity) => {
      const matches = sameOption(variant, option)
      let selection = variantOptionsAttributes(item)
      if (selection.some(matches)) {
        selection = quantity
          ? selection.map(x => (matches(x) ? { ...x, quantity } : x))
          : selection.filter(x => !matches(x))
      } else if (quantity) {
        selection.push({ variant_id: variant.id, variant_option_id: option.id, quantity })
      }
      setVariantSelection(item, selection)
    }

    // "Remove" leaves the item out of the cart the save rebuilds; a quantity of
    // zero would be dropped on the floor by the server instead.
    const setCartQuantity = (item, quantity) => {
      if (quantity === 'Remove') {
        if (staged.isTemp(item.type, item.id)) {
          staged.unlinkMany(vCart.value.type, vCart.value.id, 'cart_items', item)
          staged.forgetRecord(item.type, item.id)
        } else {
          staged.patchRecord(item.type, item.id, { attributes: { is_active: false } })
        }
      } else {
        staged.patchRecord(item.type, item.id, { attributes: { quantity: parseInt(quantity) } })
      }
      stageCart()
    }

    const filterProductsByStore = products => {
      if (!vSelectedStore.value?.id) {
        return products
      }

      const filteredProducts = products.filter(product => {
        const storePairs = product.relationships?.category_product_store_pairs?.data || []
        return storePairs.some(pair => {
          const pairObject = mapRelationship(pair)
          return parseInt(pairObject?.attributes?.store_id) === parseInt(vSelectedStore.value.id)
        })
      })

      return filteredProducts
    }

    let productionDatesFetchToken = 0
    const loadProductionDates = async storeId => {
      if (!storeId) {
        availableProductionDates.value = []
        return
      }
      const token = ++productionDatesFetchToken
      productionDatesLoading.value = true
      try {
        const from = dateObjectToServerDay(new Date())
        const to = dateObjectToServerDay(maxSelectableDate.value)
        const response = await api.call(
          'GET',
          `stores/${storeId}/production_dates?date_from=${from}&date_to=${to}`,
          {},
          { receive: false }
        )
        if (token !== productionDatesFetchToken) return
        availableProductionDates.value = (response.data.data || []).map(serverDayToDateObject)
      } catch (err) {
        if (token !== productionDatesFetchToken) return
        console.error('[production_dates] fetch failed', err)
        availableProductionDates.value = []
      } finally {
        if (token === productionDatesFetchToken) productionDatesLoading.value = false
      }
    }

    onMounted(() => {
      Promise.all([
        api.call('GET', `bottle_cycles/${drop.id}?included=${dropIncludes()}`).then(settleAfterLoad),
        api.call('GET', 'stores?included=none'),
        api.call('GET', 'membership_tiers?included=audience.audience_detail')
      ])
        .then(() => {
          drop.loading = false
          vSelectedAudiences.value.forEach(x => {
            api.call('GET', `audiences/${x.id}/audience_details`).catch(() => {})
          })
          loadProductionDates(vSelectedStore.value?.id)
        })
        // Without this the page sits on its spinner for ever: the load simply
        // never resolves, and nothing tells the merchant why.
        .catch(error => {
          drop.loading = false
          const status = error?.response?.status
          if (status === 401 || status === 404) {
            drop.missing = true
            return
          }
          showToast('That drop could not be loaded.', { type: 'error', duration: 8000 })
        })
    })

    const setModal = object => {
      modal.object = object
      if (object.type === 'fulfillment_availability') {
        modal.date = serverDayToDateObject(object.attributes.date)
        modal.time = false
      } else {
        modal.date = vScheduler(object)
        modal.time = true
      }
      if (object.type === 'bottle_reminder_automation') {
        const persisted = Boolean(object.id)
        if (!persisted) modal.date = defaultReminderDate()
        modal.message = persisted ? editableReminderText(vMessage(object)) : ''
        modal.images = persisted ? vImages(object).map((x, i) => ({ id: x.id, order: i })) : []
        modal.attachmentType = persisted && vTemplate(object).attributes.attach_vcard ? 'vcard' : 'image'
      } else {
        modal.message = null
        modal.images = []
      }
      if (object.type === 'duplicator') {
        modal.interval_number = object.attributes?.interval_number ?? DUPLICATOR_DEFAULT_INTERVAL.number
        modal.interval_type = object.attributes?.interval_type ?? DUPLICATOR_DEFAULT_INTERVAL.type
      }

      modal.visible = true
    }

    const modalExistingImages = computed(() => {
      if (!modal.object?.id || modal.object.type !== 'bottle_reminder_automation') return []
      return vImages(modal.object)
    })

    const changeModalImages = images => {
      modal.images = images
    }

    function indexForInterval(interval) {
      switch (interval) {
        case 'weeks':
          return vDuplicatorOptions.value[0]
        case 'days':
          return vDuplicatorOptions.value[1]
        default:
          return vDuplicatorOptions.value[0]
      }
    }

    const autoChargeConfirm = reactive({ open: false, datetime: null })

    const autoChargeConfirmAudiences = computed(() => audienceConfirmRows(vSelectedAudiences.value, vAudienceDetail))

    // The server keeps one autocharge per drop (update_only), so the body
    // never names it: a time is set on whichever record is there, or a new one.
    const stageAutocharge = datetime => {
      if (vAutocharge.value) {
        const scheduler = mapRelationship(vAutocharge.value.relationships.automation_scheduler.data)
        if (scheduler) staged.patchRecord(scheduler.type, scheduler.id, { attributes: { datetime } })
      } else {
        const scheduler = staged.addRecord({
          type: 'automation_scheduler',
          attributes: { frequency: 'once', datetime }
        })
        const automation = staged.addRecord({
          type: 'bottle_autocharge_automation',
          attributes: { status: 'active', is_active: true },
          relationships: { automation_scheduler: { data: recordRef(scheduler) } }
        })
        patchDrop({ relationships: { bottle_autocharge_automation: { data: recordRef(automation) } } })
      }
      stage({ bottle_autocharge_automation_attributes: { automation_scheduler_attributes: { datetime } } })
    }

    const persistAutocharge = () => {
      stageAutocharge(autoChargeConfirm.datetime)
      autoChargeConfirm.open = false
    }

    // An image is uploaded the moment it is picked and linked to the reminder
    // on save. For a reminder created on this page every image is a reference
    // to such an upload, whichever way the uploader named it.
    const newReminderImages = images =>
      images.filter(x => !x._destroy).map(({ id, _id, order }) => ({ _id: _id ?? id, order }))

    const attachmentRefs = images =>
      images.filter(x => !x._destroy).map(x => ({ type: 'attachment', id: String(x._id ?? x.id) }))

    // For a reminder the server knows, an image it already carried is named by
    // id; one uploaded since is a reference (`_id`) until the save links it,
    // however the modal named it on a second opening. Removing an image the
    // server never linked needs no entry at all.
    const persistedReminderImages = (images, template) => {
      const loaded = template ? staged.original(template.type, template.id) : null
      const linked = new Set((loaded?.relationships?.attachments?.data || []).map(x => String(x.id)))
      return images.flatMap(image => {
        if (image._id || (image.id && linked.has(String(image.id)))) return [image]
        return image._destroy ? [] : [{ _id: image.id, order: image.order }]
      })
    }

    const stageReminder = () => {
      const datetime = modal.date.toISOString()
      const body = modal.message.trim()
      const attachVcard = modal.attachmentType === 'vcard'
      const images = modal.images || []
      const templateAttributes = { body, attach_vcard: attachVcard }
      const attachments = { data: attachVcard ? [] : attachmentRefs(images) }
      const existing = modal.object.id ? staged.read('bottle_reminder_automation', modal.object.id) : null

      if (existing) {
        const scheduler = mapRelationship(existing.relationships.automation_scheduler.data)
        const template = vTemplate(existing)
        if (scheduler) staged.patchRecord(scheduler.type, scheduler.id, { attributes: { datetime } })
        if (template) {
          staged.patchRecord(template.type, template.id, {
            attributes: templateAttributes,
            relationships: { attachments }
          })
        }
        const persisted = !isLocalId(existing.id)
        stage(
          reminderEntries([
            {
              ...identity(existing),
              ...reminderAutomationAttributes({
                templateId: persisted ? template?.id : undefined,
                datetime,
                body,
                attachVcard,
                images: persisted ? persistedReminderImages(images, template) : newReminderImages(images)
              })
            }
          ])
        )
        return
      }

      const scheduler = staged.addRecord({ type: 'automation_scheduler', attributes: { frequency: 'once', datetime } })
      const template = staged.addRecord({
        type: 'message_template',
        attributes: templateAttributes,
        relationships: { attachments }
      })
      stageAddition({
        parent: dropRecord(),
        relationship: 'bottle_reminder_automations',
        record: {
          type: 'bottle_reminder_automation',
          attributes: { status: 'active', turned_on: true },
          relationships: {
            automation_scheduler: { data: recordRef(scheduler) },
            message_template: { data: recordRef(template) }
          }
        },
        entry: reminderAutomationAttributes({ datetime, body, attachVcard, images: newReminderImages(images) }),
        entries: reminderEntries
      })
    }

    const stageDay = () => {
      const date = dateObjectToServerDay(modal.date)
      const existing = modal.object.id ? staged.read('fulfillment_availability', modal.object.id) : null
      if (existing) {
        staged.patchRecord(existing.type, existing.id, { attributes: { date } })
        stage(dayEntries([{ ...identity(existing), date }]))
        return
      }
      stageAddition({
        parent: dropRecord(),
        relationship: 'fulfillment_availabilities',
        record: { type: 'fulfillment_availability', attributes: { date, limit_quantity: false, quantity_limit: 0 } },
        entry: { date },
        entries: dayEntries
      })
    }

    const stageDuplicator = () => {
      const attributes = duplicatorAttributes({ number: modal.interval_number, type: modal.interval_type })
      if (vDuplicator.value) {
        staged.patchRecord(vDuplicator.value.type, vDuplicator.value.id, { attributes })
      } else {
        const duplicator = staged.addRecord({
          type: 'duplicator',
          attributes: { ...attributes, has_duplicated: false, is_active: true }
        })
        patchDrop({ relationships: { duplicator: { data: recordRef(duplicator) } } })
      }
      stage({ duplicator_attributes: attributes })
    }

    const saveModal = () => {
      if (modal.object.type === 'bottle_autocharge_automation') {
        // Capture the chosen time, close the date picker, and require the
        // acknowledgement modal first (persistAutocharge on Confirm).
        autoChargeConfirm.datetime = modal.date.toISOString()
        modal.visible = false
        autoChargeConfirm.open = true
        return
      }
      if (modal.object.type === 'bottle_reminder_automation') stageReminder()
      else if (modal.object.type === 'fulfillment_availability') stageDay()
      else if (modal.object.type === 'duplicator') stageDuplicator()
      modal.visible = false
      hintUnsaved()
    }

    const handleDiscardChanges = () => {
      const storeChanged = 'store_id' in pending.value
      discardStaged()
      if (storeChanged) loadProductionDates(vSelectedStore.value?.id)
    }

    const handleConfirmCancelChanges = () => {
      discardStaged()
      drop.leaveModal = false
      router.push('/store/drops')
    }

    const browserTZ = computed(() =>
      new Date().toLocaleDateString(undefined, { day: '2-digit', timeZoneName: 'short' }).substring(4)
    )
    const format = x =>
      new Intl.DateTimeFormat([], { dateStyle: 'full', timeStyle: 'short' }).format(x) + ` (${browserTZ.value})`
    const formatDay = x => new Intl.DateTimeFormat([], { dateStyle: 'full' }).format(serverDayToDateObject(x))

    const availableProductionDates = ref([])
    const productionDatesLoading = ref(false)

    const maxSelectableDate = computed(() => {
      const d = new Date()
      d.setHours(0, 0, 0, 0)
      d.setDate(d.getDate() + 42)
      return d
    })

    const minSelectableDate = computed(() => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return today
    })

    const datePickerDisabledDates = computed(() => {
      if (modal.object?.type !== 'fulfillment_availability') return []
      const availableMs = new Set(availableProductionDates.value.map(d => d.getTime()))
      const disabled = []
      const cursor = new Date()
      cursor.setHours(0, 0, 0, 0)
      for (let i = 0; i <= 42; i++) {
        if (!availableMs.has(cursor.getTime())) disabled.push(new Date(cursor))
        cursor.setDate(cursor.getDate() + 1)
      }
      return disabled
    })

    const reminderTextMissing = computed(
      () => modal.object?.type === 'bottle_reminder_automation' && !hasReminderText(modal.message)
    )

    const canSaveModal = computed(() => {
      if (modal.object?.type === 'fulfillment_availability') return !!modal.date
      if (modal.object?.type === 'bottle_reminder_automation') return !reminderTextMissing.value && !!modal.date
      return true
    })

    return {
      activeTab,
      wizardEnabled,
      fulfillmentWizardPath: FULFILLMENT_WIZARD_PATH,
      dropSections,
      saveDrop,
      saving,
      hasUnsavedChanges,
      setDescription,
      deleteDrop,
      performDelete,
      showDeleteModal,
      ordersModal,
      closeOrdersModal,
      confirmOrdersModal,
      scopeModal,
      chooseScope,
      drop,
      readableAudiencesNames,
      readableRemindersTimes,
      readablePossibleFulfillmentDaysText,
      customJoin,
      reminderAutomationMessage,
      autoFillAutoChargeAutomationMessage,
      autoFillReminderAutomationMessage,
      autoChargeAutomationMessage,
      displayReminderAutomationMessage,
      displayAutoChargeAutomationMessage,
      displayAutoFillAutoChargeAutomationMessage,
      displayAutoFillReminderAutomationMessage,
      vDrop,
      storeError,
      groupsError,
      fulfillmentAvailabilityDaysError,
      errorClasses,
      vAllStores,
      vSelectedStore,
      selectStore,
      vAudiences,
      vSelectedAudiences,
      toggleAudience,
      isAudienceSelected,
      vReminders,
      vScheduler,
      vMessage,
      newReminder,
      deleteReminder,
      format,
      modal,
      setModal,
      vAutofill,
      vAutocharge,
      saveModal,
      autoChargeConfirm,
      autoChargeConfirmAudiences,
      persistAutocharge,
      audienceChangeConfirm,
      audienceChangeRows,
      confirmAudienceChange,
      cancelAudienceChange,
      deleteAutofill,
      deleteAutocharge,
      vDays,
      newDay,
      formatDay,
      deleteDay,
      updateAutofillType,
      updateTipAutofill,
      updateAutofillCheckboxes,
      isAutofillReminders,
      isAutofillAutocharge,
      vAutofillType,
      vDuplicator,
      vDuplicatorOptions,
      setDuplicatorProperty,
      intervalChanged,
      duplicatorDescription,
      duplicatorIntervalType,
      deleteDuplicator,
      showDuplicator,
      isOriginalDuplication,
      hasDuplicate,
      indexForInterval,
      duplicationText,
      handleDiscardChanges,
      handleConfirmCancelChanges,
      vAudienceResults,
      vAudienceQuery,
      vAudienceDetail,
      vAudienceFilters,
      vFilterString,
      browserTZ,
      modalExistingImages,
      changeModalImages,
      setPreference,
      cartItemsModal,
      openCartItemsModal,
      saveCartItemsModal,
      image,
      filterProductsByStore,
      vCart,
      vCartItems,
      product,
      cartItemImage,
      setCartQuantity,
      variants,
      openVariantTray,
      setOption,
      setVariantQuantity,
      availableProductionDates,
      productionDatesLoading,
      maxSelectableDate,
      minSelectableDate,
      datePickerDisabledDates,
      canSaveModal,
      reminderTextMissing,
      REMINDER_TEXT_PLACEHOLDER,
      REMINDER_TEXT_REQUIRED_WARNING,
      vSuggestedTierAudiences,
      audienceDetailOrNull
    }
  }
}
